import { ImplAPI, Req } from './ImplAPI';

/**
 * Represents a standard Shopify MoneyV2 object.
 */
export interface MoneyV2 {
  /** The decimal money amount (e.g., "50.0") */
  amount: string;
  /** The 3-letter currency code (e.g., "USD") */
  currencyCode: string;
}

/**
 * The conditions required for the discount to apply.
 * Shopify returns this as a union, so fields are optional depending on the setup.
 */
export interface DiscountMinimumRequirement {
  /** Present if the discount requires a minimum cart subtotal */
  greaterThanOrEqualToSubtotal?: MoneyV2;
  /** Present if the discount requires a minimum quantity of items (as a stringified number) */
  greaterThanOrEqualToQuantity?: string;
}

/**
 * The successful response structure sent to the frontend.
 */
export interface DiscountSuccessResponse {
  success: true;
  discount: {
    /** The title of the discount (e.g., "Free Shipping over $50") */
    title: string;
    /** The threshold required to trigger the discount */
    minimumRequirement: DiscountMinimumRequirement;
  };
}

/**
 * The error response structure sent to the frontend.
 */
export interface DiscountErrorResponse {
  success: false;
  /** A user-friendly or debugging error message */
  error: string;
}

/**
 * A union type representing the possible responses to the frontend.
 * Frontend code should check the `success` boolean to narrow the type.
 */
export type DiscountResponse = DiscountSuccessResponse | DiscountErrorResponse;

export class DiscountAPI extends ImplAPI {
  // Points to our secure Next.js backend route
  static get route() {
    return '/api/discounts/freeshipping';
  }

  /**
   * Fetches the active Free Shipping code from the Shopify Admin backend.
   */
  static async getFreeShipping(): Promise<
    DiscountResponse & { response_code: number }
  > {
    return Req<DiscountResponse & { response_code: number }>({
      method: 'GET',
      route: this.route,
      // No credentials needed if this is a public utility route,
      // but you can add credentials: 'include' if you want it protected.
    });
  }
}
